import { useEffect, useMemo, useRef, useState } from "react";
import * as echarts from "echarts";

function uniqPreserve(arr) {
  const seen = new Set();
  const out = [];
  for (const v of arr) {
    if (seen.has(v)) continue;
    seen.add(v);
    out.push(v);
  }
  return out;
}

function getPxPerUnit(chart) {
  const p00 = chart.convertToPixel({ seriesIndex: 0 }, [0, 0]);
  const p10 = chart.convertToPixel({ seriesIndex: 0 }, [1, 0]);
  const p01 = chart.convertToPixel({ seriesIndex: 0 }, [0, 1]);

  const dx = Math.abs((p10?.[0] ?? 0) - (p00?.[0] ?? 0));
  const dy = Math.abs((p01?.[1] ?? 0) - (p00?.[1] ?? 0));
  return Math.max(1, Math.min(dx || 0, dy || 0) || 1);
}

function layoutNodesStrict(names, boundMin, boundMax, minDistAxis) {
  // Fixed grid layout - no randomization
  const COLS = 3; // nodes per row
  const xStep = (boundMax - boundMin) / (COLS + 1);
  const rows = Math.ceil(names.length / COLS);
  const yStep = (boundMax - boundMin) / (rows + 1);

  const nodes = [];
  for (let i = 0; i < names.length; i++) {
    const row = Math.floor(i / COLS);
    const col = i % COLS;
    const x = boundMin + (col + 1) * xStep;
    const y = boundMin + (row + 1) * yStep;
    nodes.push({
      name: names[i],
      value: [+x.toFixed(3), +y.toFixed(3)],
    });
  }
  return nodes;
}

function isTooCloseInPixels(chart, nodes, minPx) {
  for (let i = 0; i < nodes.length; i++) {
    const pi = chart.convertToPixel({ seriesIndex: 0 }, nodes[i].value);
    if (!pi) continue;
    for (let j = i + 1; j < nodes.length; j++) {
      const pj = chart.convertToPixel({ seriesIndex: 0 }, nodes[j].value);
      if (!pj) continue;
      const dx = pi[0] - pj[0];
      const dy = pi[1] - pj[1];
      if (Math.sqrt(dx * dx + dy * dy) < minPx) return true;
    }
  }
  return false;
}

function buildArrowHead(x1, y1, x2, y2, size = 10) {
  const dx = x2 - x1;
  const dy = y2 - y1;
  const len = Math.sqrt(dx * dx + dy * dy) || 1;

  const ux = dx / len;
  const uy = dy / len;

  const px = -uy;
  const py = ux;

  const backX = x2 - ux * size;
  const backY = y2 - uy * size;

  const wing = size * 0.6;
  const pA = [x2, y2];
  const pB = [backX + px * wing, backY + py * wing];
  const pC = [backX - px * wing, backY - py * wing];

  return [pA, pB, pC];
}

function truncateText(text, maxLength = 12) {
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength) + "...";
}

function getViewportBucket() {
  if (typeof window === "undefined") return "w0-h0";

  const width = window.innerWidth || 0;
  const height = window.innerHeight || 0;
  const round = (n, step) => Math.max(step, Math.round(n / step) * step);
  return `w${round(width, 100)}-h${round(height, 100)}`;
}

const layoutCache = new Map();

function readCachedLayout(storageKey) {
  return layoutCache.get(storageKey) ?? null;
}

function writeCachedLayout(storageKey, nodes) {
  layoutCache.set(storageKey, nodes);
}

function readPersistedLayout(storageKey, itemNames) {
  try {
    const raw = localStorage.getItem(storageKey);
    if (!raw) return null;

    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return null;

    const nodes = [];
    for (const name of itemNames) {
      const v = parsed[name];
      if (!Array.isArray(v) || v.length !== 2) return null;
      const x = Number(v[0]);
      const y = Number(v[1]);
      if (!Number.isFinite(x) || !Number.isFinite(y)) return null;
      if (x < 0 || x > 10 || y < 0 || y > 10) return null;
      nodes.push({ name, value: [x, y] });
    }
    return nodes;
  } catch {
    return null;
  }
}

function writePersistedLayout(storageKey, nodes) {
  try {
    const out = {};
    for (const n of nodes) {
      out[n.name] = [n.value[0], n.value[1]];
    }
    localStorage.setItem(storageKey, JSON.stringify(out));
  } catch {
    // ignore storage errors
  }
}

export default function ConnectDotsChart({
  items,
  dispatch,
  questionState,
  isExplanation = false,
  presetOrder,
  guidePair,
  layoutStorageKey,
  persistOnAnswer,
}) {
  const safeItems = Array.isArray(items) ? items : [];

  const resolvedLayoutKey = useMemo(() => {
    if (!layoutStorageKey) return undefined;
    return `${layoutStorageKey}-${getViewportBucket()}`;
  }, [layoutStorageKey]);

  const THEME = {
    bg: "#ffffff",
    text: "#0f172a",
    line: "#0369a1",
    tempLine: "#0284c7",
    nodeNormal: "#16a34a",
    nodeUsed: "#cbd5e1",
    nodeLast: "#0284c7",
    border: "#0f172a",
    borderUsed: "#64748b",
    borderLast: "#075985",
    labelOnDark: "#ffffff",
    labelOnLight: "#0f172a",
    guideLine: "#94a3b8",
    guideArrow: "#94a3b8",
  };

  const CHART_HEIGHT = 450;
  const CHART_MAX_WIDTH = 800;

  const SYMBOL_SIZE = 50;
  const BASE_MIN_DIST_AXIS = 1.5;

  const EDGE_ARROW = ["none", "arrow"];
  const EDGE_ARROW_SIZE = 12;

  const domRef = useRef(null);
  const chartRef = useRef(null);

  const [orderText, setOrderText] = useState("Order: (belum ada)");
  const [canBack, setCanBack] = useState(false);

  const nodesRef = useRef([]);
  const seqRef = useRef([]);
  const linksRef = useRef([]);

  const draggingRef = useRef(false);
  const dragStartIndexRef = useRef(null);
  const dragStartPointRef = useRef({ x: 0, y: 0 });
  const activePointerIdRef = useRef(null);
  const dispatchTimerRef = useRef(null);

  const [hint, setHint] = useState("");
  const hintTimerRef = useRef(null);

  const showHint = (msg) => {
    setHint(msg);
    if (hintTimerRef.current) window.clearTimeout(hintTimerRef.current);
    hintTimerRef.current = window.setTimeout(() => setHint(""), 900);
  };

  const HIT_RADIUS = useMemo(() => SYMBOL_SIZE + 4, []);

  const getGuideIdxs = () => {
    if (!guidePair) return null;
    const [fromEp, toEp] = guidePair;
    const from = resolveGuideIndex(fromEp);
    const to = resolveGuideIndex(toEp);
    if (from < 0 || to < 0) return null;
    return { from, to };
  };

  const isFirstStepLocked = () => {
    return !isExplanation && seqRef.current.length === 0 && !!getGuideIdxs();
  };

  const lastInSequence = () => {
    const seq = seqRef.current;
    return seq.length ? seq[seq.length - 1] : null;
  };

  const canStartFrom = (idx) => {
    const locked = isFirstStepLocked();
    if (locked) {
      const g = getGuideIdxs();
      return g ? idx === g.from : false;
    }

    const last = lastInSequence();
    return last === null || idx === last;
  };

  const canConnectTo = (idx) => {
    const start = dragStartIndexRef.current;

    const locked = isFirstStepLocked();
    if (locked) {
      const g = getGuideIdxs();
      if (!g) return false;
      if (idx === start) return false;
      return idx === g.to;
    }

    if (idx === start) return false;
    if (seqRef.current.includes(idx)) return false;
    return true;
  };

  const rebuildLinks = () => {
    const seq = seqRef.current;
    const nodes = nodesRef.current;
    const next = [];

    if (seq.length >= 2) {
      for (let i = 0; i < seq.length - 1; i++) {
        const s = nodes[seq[i]]?.name;
        const t = nodes[seq[i + 1]]?.name;
        if (s && t) next.push({ source: s, target: t });
      }
    }
    linksRef.current = next;
  };

  const renderSequenceText = () => {
    const nodes = nodesRef.current;
    const seq = seqRef.current;

    if (seq.length === 0) {
      setOrderText("Order: (belum ada)");
      setCanBack(false);
      return;
    }

    const names = seq.map((i) => nodes[i]?.name ?? String(i));
    setOrderText(`Order: [${names.join(" -> ")}]`);
    setCanBack(true);
  };

  const commitAnswer = () => {
    const nodes = nodesRef.current;
    const seq = seqRef.current;
    const names = seq.map((i) => nodes[i]?.name).filter(Boolean);

    if (dispatchTimerRef.current) window.clearTimeout(dispatchTimerRef.current);
    dispatchTimerRef.current = window.setTimeout(() => {
      if (dispatch && questionState) {
        dispatch({
          type: "question/setQuestion",
          payload: { ...questionState, answerText: names },
        });
      }
      dispatchTimerRef.current = null;
    }, 800);
  };

  const getNodeSeriesData = () => {
    const nodes = nodesRef.current;
    const seq = seqRef.current;
    const last = lastInSequence();
    const locked = lockedFirst();
    const g = getGuideIdxs();

    return nodes.map((n, i) => {
      const used = seq.includes(i);
      const isLast = last === i;

      let color = THEME.nodeNormal;
      let borderColor = THEME.border;
      let borderWidth = 2;
      let labelColor = THEME.labelOnDark;

      let opacity = 1;
      let labelOpacity = 1;

      if (locked && g) {
        const isStartAllowed = i === g.from;
        const isTargetAllowed = i === g.to;

        if (!isStartAllowed && !isTargetAllowed) {
          opacity = 0.25;
          labelOpacity = 0.25;
          color = THEME.nodeUsed;
          borderColor = THEME.borderUsed;
          labelColor = THEME.labelOnLight;
        }

        if (isStartAllowed) {
          borderWidth = 4;
          borderColor = THEME.borderLast;
        }

        if (isTargetAllowed) {
          borderWidth = 3;
          borderColor = THEME.guideLine;
        }
      }

      if (used && !isLast) {
        color = THEME.nodeUsed;
        borderColor = THEME.borderUsed;
        labelColor = THEME.labelOnLight;
      }
      if (isLast) {
        color = THEME.nodeLast;
        borderColor = THEME.borderLast;
        borderWidth = 3;
        labelColor = THEME.labelOnDark;
      }

      return {
        name: n.name,
        value: n.value,
        itemStyle: {
          color,
          borderColor,
          borderWidth,
          opacity,
          shadowBlur: locked && g && i === g.from ? 14 : 0,
          shadowColor: locked && g && i === g.from ? THEME.tempLine : "transparent",
        },
        label: {
          show: true,
          position: "inside",
          fontSize: 11,
          width: SYMBOL_SIZE - 8,
          overflow: "truncate",
          ellipsis: "...",
          content: truncateText(n.name, 12),
          color: labelColor,
          fontWeight: 700,
          opacity: labelOpacity,
        },
      };
    });
  };

  const baseOption = useMemo(
    () => ({
      backgroundColor: THEME.bg,
      tooltip: {
        show: true,
        trigger: "item",
        backgroundColor: "rgba(0, 0, 0, 0.8)",
        borderColor: "transparent",
        textStyle: { color: "#ffffff", fontSize: 12 },
        confine: true,
        formatter: (params) => {
          if (params.componentSubType === "graph") {
            return params.name || params.value;
          }
          return params.name || "";
        },
      },
      xAxis: { type: "value", min: 0, max: 10, show: false },
      yAxis: { type: "value", min: 0, max: 10, show: false, inverse: true },
      series: [
        {
          id: "graph",
          type: "graph",
          layout: "none",
          coordinateSystem: "cartesian2d",
          symbolSize: SYMBOL_SIZE,
          draggable: false,
          roam: false,
          data: [],
          links: [],
          edgeSymbol: EDGE_ARROW,
          edgeSymbolSize: [0, EDGE_ARROW_SIZE],
          lineStyle: { width: 3, color: THEME.line, curveness: 0, opacity: 1 },
          emphasis: { focus: "none", scale: true },
          blur: {
            itemStyle: { opacity: 1 },
            lineStyle: { opacity: 1 },
            label: { opacity: 1 },
          },
        },
      ],
      graphic: [],
    }),
    []
  );

  const resolveGuideIndex = (ep) => {
    if (typeof ep === "number") return ep;
    return nodesRef.current.findIndex((n) => n.name === ep);
  };

  const lockedFirst = () =>
    !isExplanation && seqRef.current.length === 0 && !!getGuideIdxs();

  const getGuideGraphic = () => {
    const chart = chartRef.current;
    if (!chart || !guidePair || isExplanation) return [];

    const [fromEp, toEp] = guidePair;
    const i1 = resolveGuideIndex(fromEp);
    const i2 = resolveGuideIndex(toEp);

    if (i1 < 0 || i2 < 0) return [];
    if (!nodesRef.current[i1] || !nodesRef.current[i2]) return [];

    const p1 = chart.convertToPixel({ seriesIndex: 0 }, nodesRef.current[i1].value);
    const p2 = chart.convertToPixel({ seriesIndex: 0 }, nodesRef.current[i2].value);
    if (!p1 || !p2) return [];

    const dx = p2[0] - p1[0];
    const dy = p2[1] - p1[1];
    const len = Math.sqrt(dx * dx + dy * dy) || 1;
    const ux = dx / len;
    const uy = dy / len;

    const GUIDE_ARROW_SIZE = 12;
    const GUIDE_GAP_PX = 1;

    const stop = SYMBOL_SIZE / 2 + GUIDE_GAP_PX;
    const endX = p2[0] - ux * stop;
    const endY = p2[1] - uy * stop;

    return [
      {
        id: "guide-edge",
        type: "line",
        silent: true,
        shape: { x1: p1[0], y1: p1[1], x2: endX, y2: endY },
        style: {
          stroke: THEME.guideLine,
          lineWidth: 2,
          lineDash: [6, 6],
          lineCap: "round",
          opacity: 1,
        },
        z: 2,
      },
      {
        id: "guide-arrow",
        type: "polygon",
        silent: true,
        shape: { points: buildArrowHead(p1[0], p1[1], endX, endY, GUIDE_ARROW_SIZE) },
        style: { fill: THEME.guideArrow, opacity: 1 },
        z: 3,
      },
    ];
  };

  const renderChart = () => {
    const chart = chartRef.current;
    if (!chart) return;

    chart.setOption(
      {
        ...baseOption,
        series: [
          {
            ...(baseOption.series?.[0]),
            data: getNodeSeriesData(),
            links: linksRef.current,
          },
        ],
        graphic: [...getGuideGraphic()],
      },
      true
    );
    renderSequenceText();
  };

  const updateGraph = () => {
    const chart = chartRef.current;
    if (!chart) return;

    chart.setOption(
      {
        series: [
          {
            id: "graph",
            type: "graph",
            layout: "none",
            coordinateSystem: "cartesian2d",
            symbolSize: SYMBOL_SIZE,
            draggable: false,
            roam: false,
            data: getNodeSeriesData(),
            links: [...linksRef.current],
            edgeSymbol: EDGE_ARROW,
            edgeSymbolSize: [0, EDGE_ARROW_SIZE],
            lineStyle: { width: 3, color: THEME.line, curveness: 0, opacity: 1 },
            emphasis: { focus: "none", scale: true },
            blur: {
              itemStyle: { opacity: 1 },
              lineStyle: { opacity: 1 },
              label: { opacity: 1 },
            },
          },
        ],
        graphic: [...getGuideGraphic()],
      },
      { replaceMerge: ["series", "graphic"] }
    );

    renderSequenceText();
  };

  const getNodePixelPos = (idx) => {
    const chart = chartRef.current;
    const nodes = nodesRef.current;
    if (!chart) return null;
    const p = chart.convertToPixel({ seriesIndex: 0 }, nodes[idx].value);
    if (!p) return null;
    return { x: p[0], y: p[1] };
  };

  const findNodeIndexAtPixel = (x, y) => {
    const nodes = nodesRef.current;
    for (let i = 0; i < nodes.length; i++) {
      const p = getNodePixelPos(i);
      if (!p) continue;
      const dx = x - p.x;
      const dy = y - p.y;
      if (Math.sqrt(dx * dx + dy * dy) <= HIT_RADIUS) return i;
    }
    return null;
  };

  const updateTempEdge = (x2, y2) => {
    const chart = chartRef.current;
    if (!chart || !draggingRef.current) return;

    chart.setOption(
      {
        graphic: [
          ...getGuideGraphic(),
          {
            id: "temp-edge",
            type: "line",
            silent: true,
            shape: {
              x1: dragStartPointRef.current.x,
              y1: dragStartPointRef.current.y,
              x2,
              y2,
            },
            style: { stroke: THEME.tempLine, lineWidth: 2, opacity: 1, lineCap: "round" },
            z: 100,
          },
        ],
      },
      { replaceMerge: ["graphic"] }
    );
  };

  const clearTempEdge = () => {
    const chart = chartRef.current;
    if (!chart) return;
    chart.setOption(
      { graphic: [...getGuideGraphic()] },
      { replaceMerge: ["graphic"] }
    );
  };

  const resetDragState = () => {
    draggingRef.current = false;
    dragStartIndexRef.current = null;
    activePointerIdRef.current = null;
    clearTempEdge();
  };

  const doBack = () => {
    if (seqRef.current.length === 0) return;

    seqRef.current.pop();
    rebuildLinks();
    resetDragState();
    updateGraph();
    commitAnswer();
  };

  useEffect(() => {
    if (!chartRef.current) return;
    updateGraph();
  }, [guidePair ? String(guidePair[0]) + "|" + String(guidePair[1]) : ""]);

  useEffect(() => {
    const dom = domRef.current;
    if (!dom || safeItems.length === 0) return;

    dom.style.touchAction = "none";

    const chart = echarts.init(dom, undefined, {
      devicePixelRatio: window.devicePixelRatio || 1,
    });
    chartRef.current = chart;

    chart.setOption(baseOption, true);

    const pxPerUnit = getPxPerUnit(chart);

    const desiredPx = SYMBOL_SIZE + 12;
    const effectiveMinDist = Math.max(BASE_MIN_DIST_AXIS, desiredPx / pxPerUnit);

    const marginAxis = (SYMBOL_SIZE / 2 + 8) / pxPerUnit;

    const boundMin = Math.max(0.2, marginAxis);
    const boundMax = Math.min(9.8, 10 - marginAxis);

    const restoredNodes = resolvedLayoutKey
      ? readCachedLayout(resolvedLayoutKey) || readPersistedLayout(resolvedLayoutKey, safeItems)
      : null;

    nodesRef.current = restoredNodes ?? layoutNodesStrict(safeItems, boundMin, boundMax, effectiveMinDist);
    if (!restoredNodes && resolvedLayoutKey) {
      writeCachedLayout(resolvedLayoutKey, nodesRef.current);
      writePersistedLayout(resolvedLayoutKey, nodesRef.current);
    }

    const coerceStringArray = (v) => {
      if (!Array.isArray(v)) return [];
      return v
        .map((x) => (x == null ? "" : String(x).trim()))
        .filter((x) => x.length > 0);
    };

    const initialNames =
      isExplanation && presetOrder?.length
        ? presetOrder
        : coerceStringArray(questionState?.answerText);

    const idxs = uniqPreserve(initialNames.map((nm) => safeItems.indexOf(nm)).filter((i) => i >= 0));
    seqRef.current = idxs;
    rebuildLinks();
    renderChart();

    const onResize = () => chart.resize();
    window.addEventListener("resize", onResize);

    const ro = new ResizeObserver(() => {
      chart.resize();

      if (isExplanation) return;
      if (seqRef.current.length > 0) return;
      if (draggingRef.current) return;

      const pxPerUnit = getPxPerUnit(chart);
      const desiredPx = SYMBOL_SIZE + 12;

      if (!isTooCloseInPixels(chart, nodesRef.current, desiredPx)) return;

      const effectiveMinDist = Math.max(BASE_MIN_DIST_AXIS, desiredPx / pxPerUnit);
      const marginAxis = (SYMBOL_SIZE / 2 + 8) / pxPerUnit;
      const boundMin = Math.max(0.2, marginAxis);
      const boundMax = Math.min(9.8, 10 - marginAxis);

      nodesRef.current = layoutNodesStrict(safeItems, boundMin, boundMax, effectiveMinDist);
      if (resolvedLayoutKey) {
        writeCachedLayout(resolvedLayoutKey, nodesRef.current);
        writePersistedLayout(resolvedLayoutKey, nodesRef.current);
      }
      rebuildLinks();
      updateGraph();
    });
    ro.observe(dom);

    if (isExplanation) {
      return () => {
        ro.disconnect();
        window.removeEventListener("resize", onResize);
        if (dispatchTimerRef.current) window.clearTimeout(dispatchTimerRef.current);
        chart.dispose();
        chartRef.current = null;
      };
    }

    const clamp = (n, min, max) => Math.max(min, Math.min(max, n));
    const getRelativePos = (evt) => {
      const rect = dom.getBoundingClientRect();
      return {
        x: clamp(evt.clientX - rect.left, 0, rect.width),
        y: clamp(evt.clientY - rect.top, 0, rect.height),
      };
    };

    const handlePointerDown = (evt) => {
      if (evt.pointerType === "mouse" && evt.button !== 0) return;
      if (activePointerIdRef.current !== null) return;

      const { x, y } = getRelativePos(evt);
      const idx = findNodeIndexAtPixel(x, y);
      if (idx === null) return;
      if (!canStartFrom(idx)) {
        const g = getGuideIdxs();
        if (lockedFirst() && g) {
          showHint(`Mulai dari "${nodesRef.current[g.from]?.name}" dulu.`);
        } else {
          showHint(`Lanjutkan dari node terakhir.`);
        }
        return;
      }

      const p = getNodePixelPos(idx);
      if (!p) return;

      evt.preventDefault();
      activePointerIdRef.current = evt.pointerId;
      draggingRef.current = true;
      dragStartIndexRef.current = idx;
      dragStartPointRef.current = { x: p.x, y: p.y };
      updateTempEdge(p.x, p.y);
    };

    const handlePointerMove = (evt) => {
      if (!draggingRef.current) return;
      if (activePointerIdRef.current !== null && evt.pointerId !== activePointerIdRef.current) return;

      evt.preventDefault();
      const { x, y } = getRelativePos(evt);
      updateTempEdge(x, y);
    };

    const endDrag = (evt) => {
      if (!draggingRef.current) return;
      if (activePointerIdRef.current !== null && evt.pointerId !== activePointerIdRef.current) return;

      const { x, y } = getRelativePos(evt);
      const endIndex = findNodeIndexAtPixel(x, y);

      if (endIndex !== null && canConnectTo(endIndex)) {
        const start = dragStartIndexRef.current;
        if (start != null) {
          if (seqRef.current.length === 0) seqRef.current = [start, endIndex];
          else seqRef.current = [...seqRef.current, endIndex];
          rebuildLinks();
        }
      } else {
        const g = getGuideIdxs();
        if (lockedFirst() && g) {
          showHint(`Ikuti garis bantu: "${nodesRef.current[g.from]?.name}" → "${nodesRef.current[g.to]?.name}".`);
        }
      }

      draggingRef.current = false;
      dragStartIndexRef.current = null;
      activePointerIdRef.current = null;

      clearTempEdge();
      updateGraph();
      commitAnswer();
    };

    dom.addEventListener("pointerdown", handlePointerDown);
    window.addEventListener("pointermove", handlePointerMove, { passive: false });
    window.addEventListener("pointerup", endDrag);
    window.addEventListener("pointercancel", endDrag);

    return () => {
      ro.disconnect();
      dom.removeEventListener("pointerdown", handlePointerDown);
      window.removeEventListener("pointermove", handlePointerMove);
      window.removeEventListener("pointerup", endDrag);
      window.removeEventListener("pointercancel", endDrag);

      window.removeEventListener("resize", onResize);
      if (dispatchTimerRef.current) window.clearTimeout(dispatchTimerRef.current);
      if (hintTimerRef.current) window.clearTimeout(hintTimerRef.current);
      chart.dispose();
      chartRef.current = null;
    };
  }, [safeItems.join("|"), isExplanation, resolvedLayoutKey]);

  useEffect(() => {
    if (!isExplanation) return;
    if (!chartRef.current) return;
    if (!presetOrder?.length) return;

    const idxs = uniqPreserve(presetOrder.map((nm) => safeItems.indexOf(nm)).filter((i) => i >= 0));
    seqRef.current = idxs;
    rebuildLinks();
    updateGraph();
  }, [isExplanation, (presetOrder ?? []).join("|"), safeItems.join("|")]);

  return (
    <div style={{ width: "100%", display: "flex", flexDirection: "column", alignItems: "center", gap: "9.6px" }}>
      <div
        ref={domRef}
        style={{
          width: "100%",
          maxWidth: CHART_MAX_WIDTH,
          height: CHART_HEIGHT,
          borderRadius: "8px",
          boxShadow: "0 0 10px rgba(0,0,0,.5)",
          background: THEME.bg,
          overflow: "hidden",
        }}
      />

      <div style={{ minHeight: "20px" }}>
        {hint ? (
          <div
            style={{
              paddingLeft: "9.6px",
              paddingRight: "9.6px",
              paddingTop: "4.8px",
              paddingBottom: "4.8px",
              borderRadius: "999px",
              background: "#e2e8f0",
              color: "#0f172a",
              fontSize: "13px",
              fontWeight: 700,
            }}
          >
            {hint}
          </div>
        ) : null}
      </div>

      {!(isExplanation || !canBack) && (
        <div
          style={{
            width: "100%",
            maxWidth: CHART_MAX_WIDTH,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <button
            onClick={doBack}
            style={{
              borderRadius: "999px",
              fontWeight: 700,
              background: "#38bdf8",
              color: "#020617",
              border: "1px solid #38bdf8",
              padding: "6px 12px",
              cursor: "pointer",
              fontSize: "14px",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = "#7dd3fc";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = "#38bdf8";
            }}
          >
            ⏪ Back
          </button>
        </div>
      )}
    </div>
  );
}
